import React, { useEffect, useState } from "react";
import { X } from "lucide-react";

interface UpgradeToastProps {
  title: string;
  description: string;
  onClose?: () => void;
  duration?: number;
}

export const UpgradeToast: React.FC<UpgradeToastProps> = ({
  title,
  description,
  onClose,
  duration = 4000,
}) => {
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      handleClose();
    }, duration);

    return () => clearTimeout(timer);
  }, [duration]);

  const handleClose = () => {
    setVisible(false);
    setTimeout(() => {
      onClose?.();
    }, 300); // Wait for animation to complete
  };

  if (!visible) {
    return null;
  }

  return (
    <div className="fixed top-4 right-4 z-[100] animate-in slide-in-from-top-2 duration-300">
      <div className="bg-white border border-gray-200 rounded-md shadow-lg p-4 max-w-[350px]">
        <div className="flex items-start gap-3">
          <div className="flex-1">
            <div className="text-sm font-semibold text-gray-900">{title}</div>
            <div className="text-sm text-gray-700 mt-1">{description}</div>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 p-1 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

// Hook to show upgrade toast
export const useUpgradeToast = () => {
  const [toasts, setToasts] = useState<
    Array<{ id: string; title: string; description: string }>
  >([]);

  const showUpgradeToast = (title: string, description: string) => {
    const id = Math.random().toString(36).substr(2, 9);
    setToasts((prev) => [...prev, { id, title, description }]);
  };

  const removeToast = (id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  };

  const ToastContainer = () => (
    <>
      {toasts.map((toast) => (
        <UpgradeToast
          key={toast.id}
          title={toast.title}
          description={toast.description}
          onClose={() => removeToast(toast.id)}
        />
      ))}
    </>
  );

  return { showUpgradeToast, ToastContainer };
};
